import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft,
  Heart,
  Target,
  Trophy,
  CheckCircle,
  Plus,
  Sparkles,
  Users,
  User,
  Activity,
  Zap,
  Moon,
  Smile,
  Shirt,
  Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOING_IT_FOR_OPTIONS = [
  { value: "kids", label: "My Kids", icon: Users, desc: "Being a role model", emoji: "👨‍👧‍👦" },
  { value: "spouse", label: "My Partner", icon: Heart, desc: "Being there for them", emoji: "❤️" },
  { value: "parents", label: "My Parents", icon: Users, desc: "Making them proud", emoji: "👨‍👩‍👧" },
  { value: "self", label: "Myself", icon: User, desc: "Building discipline", emoji: "💪" },
  { value: "health_condition", label: "My Health", icon: Activity, desc: "This is survival", emoji: "🏥" }
];

const NON_SCALE_WINS = [
  { value: "energy", label: "More Energy", icon: Zap, color: "text-yellow-500" },
  { value: "sleep", label: "Better Sleep", icon: Moon, color: "text-purple-500" },
  { value: "confidence", label: "More Confidence", icon: Sparkles, color: "text-pink-500" },
  { value: "strength", label: "Getting Stronger", icon: Trophy, color: "text-orange-500" },
  { value: "mood", label: "Better Mood", icon: Smile, color: "text-green-500" },
  { value: "clothing_fit", label: "Clothes Fit Better", icon: Shirt, color: "text-blue-500" }
];

export default function Goals() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [goals, setGoals] = useState({
    why_started: "",
    doing_it_for: "",
    non_scale_wins: []
  });
  
  const [recentWins, setRecentWins] = useState([]);
  const [showWinModal, setShowWinModal] = useState(false);
  const [selectedWin, setSelectedWin] = useState(null);
  const [winNotes, setWinNotes] = useState("");

  useEffect(() => {
    fetchGoals();
  }, []);

  const fetchGoals = async () => {
    try {
      const [goalsRes, winsRes] = await Promise.all([
        axios.get(`${API}/goals/personal`),
        axios.get(`${API}/goals/non-scale-wins`)
      ]);
      
      if (goalsRes.data.goals) {
        setGoals({
          why_started: goalsRes.data.goals.why_started || "",
          doing_it_for: goalsRes.data.goals.doing_it_for || "",
          non_scale_wins: goalsRes.data.goals.non_scale_wins || []
        });
      }
      setRecentWins(winsRes.data.wins || []);
    } catch (error) {
      console.error("Failed to fetch goals:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGoals = async () => {
    if (!goals.why_started || !goals.doing_it_for) {
      toast.error("Please fill in your Why and who you're doing it for");
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/goals/personal`, goals);
      toast.success("Goals saved! Coach Coop will use this to motivate you.");
    } catch (error) {
      toast.error("Failed to save goals");
    } finally {
      setSaving(false);
    }
  };

  const handleLogWin = async () => {
    if (!selectedWin) return;
    
    try {
      await axios.post(`${API}/goals/non-scale-win`, {
        type: selectedWin,
        notes: winNotes
      });
      toast.success("Win logged! Keep stacking those victories! 🔥");
      setShowWinModal(false);
      setSelectedWin(null);
      setWinNotes("");
      fetchGoals();
    } catch (error) {
      toast.error("Failed to log win");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary animate-pulse text-2xl font-bold">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" /> Back
            </Link>
            <h1 className="font-bold tracking-tight uppercase">Personal Goals</h1>
            <div className="w-16" />
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 max-w-4xl mx-auto">
        {/* Your Why Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Card className="bg-card border-primary/30">
            <CardHeader>
              <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
                <Heart className="h-5 w-5 text-primary" />
                Your Personal Why
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                This will appear on your dashboard daily and Coach Coop will use it in your motivation messages.
              </p>
              <Textarea
                data-testid="goals-why-input"
                placeholder="I'm doing this because... I want to be healthy for my kids, I want to prove to myself I can do hard things, I want to feel confident again..."
                value={goals.why_started}
                onChange={(e) => setGoals(prev => ({ ...prev, why_started: e.target.value }))}
                className="min-h-[120px] bg-input border-transparent focus:border-primary rounded-sm"
              />
            </CardContent>
          </Card>
        </motion.div>

        {/* Who Are You Doing This For */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
                <Users className="h-5 w-5 text-accent" />
                Who Are You Doing This For?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {DOING_IT_FOR_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  const isSelected = goals.doing_it_for === option.value;
                  
                  return (
                    <motion.div
                      key={option.value}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Card
                        data-testid={`goals-for-${option.value}`}
                        onClick={() => setGoals(prev => ({ ...prev, doing_it_for: option.value }))}
                        className={`cursor-pointer transition-all ${
                          isSelected
                            ? "border-primary bg-primary/10"
                            : "border-border hover:border-primary/50"
                        }`}
                      >
                        <CardContent className="p-4 text-center">
                          <div className="text-3xl mb-2">{option.emoji}</div>
                          <div className="font-bold uppercase tracking-tight text-sm">{option.label}</div>
                          <div className="text-xs text-muted-foreground">{option.desc}</div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Save Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <Button
            data-testid="goals-save-btn"
            onClick={handleSaveGoals}
            disabled={saving}
            className="w-full bg-primary hover:bg-primary/90 uppercase font-bold tracking-widest rounded-sm py-6"
          >
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save My Goals"}
          </Button>
        </motion.div>

        {/* Non-Scale Wins Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="bg-card border-border">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-yellow-500" />
                  Non-Scale Wins
                </CardTitle>
                <Button
                  data-testid="log-win-btn"
                  size="sm"
                  onClick={() => setShowWinModal(true)}
                  className="bg-accent text-black hover:bg-accent/90 rounded-sm"
                >
                  <Plus className="h-4 w-4 mr-1" /> Log Win
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Track victories that aren't about the scale - energy, sleep, confidence, and more!
              </p>
              
              {recentWins.length > 0 ? (
                <div className="space-y-3">
                  {recentWins.slice(0, 10).map((win, i) => {
                    const winType = NON_SCALE_WINS.find(w => w.value === win.type);
                    const Icon = winType?.icon || Trophy;
                    
                    return (
                      <motion.div
                        key={win.id || i}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.05 }}
                        className="flex items-center gap-3 p-3 bg-secondary/50 rounded-sm"
                      >
                        <div className={`w-10 h-10 rounded-full bg-secondary flex items-center justify-center ${winType?.color || 'text-yellow-500'}`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <div className="font-bold uppercase tracking-tight text-sm">{winType?.label || win.type}</div>
                          {win.notes && <div className="text-xs text-muted-foreground">{win.notes}</div>}
                          <div className="text-xs text-muted-foreground">Day {win.day}</div>
                        </div>
                        <CheckCircle className="h-5 w-5 text-accent" />
                      </motion.div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Trophy className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No wins logged yet. Start tracking your victories!</p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </main>

      {/* Log Win Modal */}
      <AnimatePresence>
        {showWinModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="w-full max-w-md"
            >
              <Card className="bg-card border-accent">
                <CardHeader>
                  <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-yellow-500" />
                    Log a Win
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    What non-scale victory did you notice today?
                  </p>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {NON_SCALE_WINS.map((win) => {
                      const Icon = win.icon;
                      const isSelected = selectedWin === win.value;
                      
                      return (
                        <Card
                          key={win.value}
                          data-testid={`win-option-${win.value}`}
                          onClick={() => setSelectedWin(win.value)}
                          className={`cursor-pointer transition-all ${
                            isSelected
                              ? "border-accent bg-accent/10"
                              : "border-border hover:border-accent/50"
                          }`}
                        >
                          <CardContent className="p-3 text-center">
                            <Icon className={`h-6 w-6 mx-auto mb-1 ${win.color}`} />
                            <div className="text-xs font-bold uppercase tracking-tight">{win.label}</div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                  
                  <Textarea
                    placeholder="Any notes? (optional)"
                    value={winNotes}
                    onChange={(e) => setWinNotes(e.target.value)}
                    className="bg-input border-transparent focus:border-accent rounded-sm"
                  />
                  
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowWinModal(false);
                        setSelectedWin(null);
                        setWinNotes("");
                      }}
                      className="flex-1 rounded-sm"
                    >
                      Cancel
                    </Button>
                    <Button
                      data-testid="confirm-win-btn"
                      onClick={handleLogWin}
                      disabled={!selectedWin}
                      className="flex-1 bg-accent text-black hover:bg-accent/90 rounded-sm"
                    >
                      Log Win
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
